/* @component id="auth-button" name="AuthButton" type="ui-auth" version="2.0.1" */
"use client";
import React from "react";
import { signIn, signOut } from "next-auth/react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import useAuthorization from "@/auth/useAuth";
import Link from "next/link";
import { CircleUserRound, LogOut, Menu, UserCircle } from "lucide-react";

function getInitials(name?: string | null, email?: string | null) {
  const cleanName = name?.trim();

  if (cleanName) {
    const parts = cleanName.split(/\s+/).filter(Boolean);

    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }

    return parts[0]?.slice(0, 2).toUpperCase() ?? "U";
  }

  return email?.slice(0, 2).toUpperCase() ?? "U";
}

function MicrosoftMark() {
  return (
    <span
      aria-hidden="true"
      className="grid h-4 w-4 shrink-0 grid-cols-2 gap-0.5"
    >
      <span className="bg-[#f25022]" />
      <span className="bg-[#7fba00]" />
      <span className="bg-[#00a4ef]" />
      <span className="bg-[#ffb900]" />
    </span>
  );
}

export default function AuthButton() {
  const { session } = useAuthorization();

  if (session) {
    const userName = session.user?.name?.trim() || "Signed in user";
    const userEmail = session.user?.email?.trim() || "No email available";
    const initials = getInitials(session.user?.name, session.user?.email);

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 rounded-full"
            aria-label="Open profile menu"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full border bg-muted text-xs font-semibold">
              {initials}
            </span>
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-72">
          <div className="px-3 py-3">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border bg-muted text-xs font-semibold">
                {initials}
              </div>

              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium leading-5">
                  {userName}
                </div>

                <div className="truncate text-xs leading-5 text-muted-foreground">
                  {userEmail}
                </div>
              </div>
            </div>
          </div>

          <DropdownMenuSeparator />

          <DropdownMenuItem asChild>
            <Link
              href="https://support.gazl.co/hc/en-us/requests"
              target="_blank"
              className="w-full"
            >
              <UserCircle className="mr-2 h-4 w-4" />
              My Requests
            </Link>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem
            onClick={() =>
              signOut({
                callbackUrl: `https://login.microsoftonline.com/common/oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURIComponent(
                  window.location.origin,
                )}`,
              })
            }
            className="text-destructive focus:text-destructive"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <Button
      variant="ghost"
      className="h-9 gap-2 px-3 font-semibold"
      onClick={() => signIn("azure-ad")}
    >
      <CircleUserRound className="h-5 w-5" />
      Sign in
    </Button>
  );
}

export function MenuAuthButton() {
  return (
    <Button
      className="h-11 w-full gap-2 rounded-md bg-neutral-950 text-sm font-semibold text-white shadow-sm hover:bg-neutral-800"
      onClick={() => signIn("azure-ad")}
    >
      <MicrosoftMark />
      Sign in with Microsoft
    </Button>
  );
}
